import { Button } from '@/shared/ui/primitives';
import type { ReactNode } from 'react';

import styles from './ChipSelect.module.css';

export interface ChipOption<TValue extends string> {
  label: string;
  value: TValue;
  before?: ReactNode;
}

interface ChipSelectProps<TValue extends string> {
  options: ChipOption<TValue>[];
  value: TValue;
  onChange: (value: TValue) => void;
  ariaLabel?: string;
  testId?: string;
  chipTestId?: (value: TValue) => string;
  flush?: boolean;
  fill?: boolean;
  disabled?: boolean;
}

export const ChipSelect = <TValue extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  testId,
  chipTestId,
  flush = false,
  fill = false,
  disabled = false,
}: ChipSelectProps<TValue>) => {
  const scrollerClassName = [
    styles.scroller,
    flush && styles.flush,
    fill && styles.fill,
    disabled && styles.disabled,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={scrollerClassName}
      data-testid={testId}
      role="group"
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
    >
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <Button
            key={option.value}
            variant={isActive ? 'primary' : 'secondary'}

            type="button"
            disabled={disabled}
            aria-pressed={isActive}
            className={styles.chip}
            data-testid={chipTestId?.(option.value)}
            leading={
              option.before ? <span className={styles.iconSlot}>{option.before}</span> : null
            }
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </Button>
        );
      })}
    </div>
  );
};
